/**
 * Saker V1.2's contract (issue #286, handoff docs/design/saker-v1-handoff/HANDOFF.md at e5d63de1a): which
 * population a count, a list, a search and a detail are about. One function decides membership, so the tab
 * counts, the work areas, the queue, the detail's pager, «Andre perioder» and Kontroll's records agree.
 *
 * - One task is one manager decision (the adapter groups related conditions, reviewBarAdapter.ts casesOf).
 * - Information-only entries (`info`) are never counted; they are listed apart under «Opplysninger uten handling».
 * - Period: a dated case belongs to the period on screen when its date is one of the days shown; «Andre perioder»
 *   is exactly the rest. A worklist is split by its records' dates, so each side holds only its own records.
 * - Team: a person's case belongs to a team when the person is in it; a worklist keeps the team's records and says
 *   how many remain in other teams. Cases about nobody in particular (staffing, the pay month) show in every team.
 *
 * Pure; tested in saker.test.ts. Deferral and the Ferdig list are the company's, on the server
 * (`web/src/api/saker.ts`, table saker_task_events); this file reads them (`deferralsOf`, `doneOf`).
 */
// type only: a runtime import of '@/api/*' here would pull the API client into auth/store.ts through forgetSaker
import type { TaskState } from '@/api/saker';
import { T } from '@/i18n';
import type { CaseCat, ReviewCase } from './reviewBar.types';

export type SakerTab = 'open' | 'waiting' | 'deferred' | 'done';
export const TABS: [SakerTab, string][] = [
  ['open', 'Å følge opp'],
  ['waiting', 'Venter'],
  ['deferred', 'Utsatt'],
  ['done', 'Ferdig'],
];
export const isTab = (v: string | null): v is SakerTab => TABS.some(([k]) => k === v);

/** The four work areas (S01). Requests are decisions about the plan, so they sit under Vaktplan. */
export type Area = 'na' | 'timer' | 'planlegg' | 'lonn';
export const AREA_OF: Record<CaseCat, Area> = {
  na: 'na',
  timer: 'timer',
  foresporsler: 'planlegg',
  planlegg: 'planlegg',
  lonn: 'lonn',
};
export const isArea = (v: string | null): v is Area =>
  v === 'na' || v === 'timer' || v === 'planlegg' || v === 'lonn';

export type Period = 'current' | 'other';

export interface Scope {
  /** the days the plan shows, YYYY-MM-DD */
  days: string[];
  /** the chosen team's members, or null for Alle team */
  team: { name: string; has: (userId: number) => boolean } | null;
}

/** A worklist's record or a dated case, in the period on screen. */
const inDays = (d: string | undefined, days: string[]) => !!d && days.includes(d);

/**
 * The case as this scope sees it, or null when none of it is here. A worklist keeps only the records in the
 * period and team, with how many of the period's records remain in other teams (S10).
 */
export function scoped(c: ReviewCase, scope: Scope, period: Period): ReviewCase | null {
  const here = (d: string | undefined) =>
    period === 'current' ? inDays(d, scope.days) : !inDays(d, scope.days);
  const team = scope.team;
  if (c.members?.length) {
    const inPeriod = c.members.filter((m) => here(m.date));
    if (!inPeriod.length) return null;
    const mine = team ? inPeriod.filter((m) => m.userId != null && team.has(m.userId)) : inPeriod;
    if (!mine.length) return null;
    return {
      ...c,
      // the list's own title counts all of it; a part of it says which part, as «x av y» (#286)
      ...(mine.length !== inPeriod.length
        ? { what: T('{n} av {m} registreringer i dette utvalget', { n: mine.length, m: inPeriod.length }) }
        : {}),
      members: mine,
      records: mine.length,
      elsewhere: inPeriod.length - mine.length,
      approve: c.approve ? c.approve.filter((id) => mine.some((m) => m.id === id)) : undefined,
    };
  }
  // undated: about the company or a person, not a day; it belongs to the period on screen
  if (c.date ? !here(c.date) : period === 'other') return null;
  if (team && c.person && !team.has(c.person.id)) return null;
  return c;
}

export interface Lists {
  /** decisions the manager can take now: counted */
  open: ReviewCase[];
  waiting: ReviewCase[];
  deferred: ReviewCase[];
  /** information only: listed apart, never counted (S01) */
  info: ReviewCase[];
}

/** Every list of one scope and period from the page's cases; `deferredKeys` are the ones put off. */
export function listsOf(
  all: { cases: ReviewCase[]; waiting: ReviewCase[] },
  scope: Scope,
  period: Period,
  deferredKeys: Set<string>,
): Lists {
  const s = (xs: ReviewCase[]) => xs.map((c) => scoped(c, scope, period)).filter((c): c is ReviewCase => !!c);
  const cases = s(all.cases);
  return {
    open: cases.filter((c) => !c.info && !deferredKeys.has(c.key)),
    deferred: cases.filter((c) => !c.info && deferredKeys.has(c.key)),
    info: cases.filter((c) => c.info),
    waiting: s(all.waiting),
  };
}

/** What a search matches: the person, the task's words, its date and its area. */
export function matches(c: ReviewCase, q: string, areaName: (a: Area) => string): boolean {
  const needle = q.trim().toLocaleLowerCase('nb');
  if (!needle) return true;
  return [c.title, c.what, c.description, c.person?.name, c.dateLabel, c.dateText, areaName(AREA_OF[c.cat])]
    .filter(Boolean)
    .join(' ')
    .toLocaleLowerCase('nb')
    .includes(needle);
}

/**
 * A finished task, as the Ferdig tab lists it. The server keeps the key, the dates, the team, the work area,
 * who finished it and when (`saker_task_events`, #286 S15); who it was about and what was saved (a name, an
 * absence type, a reason) stay in this page's memory only and are gone on a reload or a log-out.
 */
export interface DoneTask {
  key: string;
  /** the server's row, the version a reopen is sent against */
  id?: number | undefined;
  /** the date the task was about, for its period; undefined when it had none */
  date?: string | undefined;
  /** the team it was finished for, null for Alle team */
  team: string | null;
  area: Area;
  /** when, ISO */
  at: string;
  /** who finished it, by name */
  by?: string | null | undefined;
  /** in memory only */
  title?: string | undefined;
  person?: string | undefined;
  result?: string | undefined;
}

/** A deferral: the date to follow up, YYYY-MM-DD. On that day the task is back under Å følge opp (S14). */
export type Deferrals = Record<string, string>;
export const dueDeferrals = (d: Deferrals, today: string): Set<string> =>
  new Set(
    Object.entries(d)
      .filter(([, on]) => on > today)
      .map(([k]) => k),
  );

/** The latest row per task and team, as a lookup: `${key}\u0000${team}` → row. */
export const stateKey = (key: string, team: string | null | undefined) => `${key}\u0000${team ?? ''}`;
export function latestOf(rows: TaskState[] | undefined): Map<string, TaskState> {
  const m = new Map<string, TaskState>();
  for (const r of rows ?? []) {
    const k = stateKey(r.key, r.team);
    const had = m.get(k);
    if (!had || had.id < r.id) m.set(k, r);
  }
  return m;
}

/** The tasks put off now, from the server's latest rows: a deferral is for every team. */
export function deferralsOf(rows: TaskState[] | undefined): Deferrals {
  const out: Deferrals = {};
  for (const r of latestOf(rows).values())
    if (r.action === 'defer' && r.team == null && r.due) out[r.key] = r.due;
  return out;
}

/** the words of a finished task, this page's memory only, per owner (company and person) */
const said = new Map<string, Map<string, Pick<DoneTask, 'title' | 'person' | 'result'>>>();
export function rememberDone(owner: string | undefined, t: DoneTask) {
  const words = said.get(owner ?? '') ?? new Map();
  words.set(stateKey(t.key, t.team), { title: t.title, person: t.person, result: t.result });
  said.set(owner ?? '', words);
}

/** The finished tasks, newest first, from the server's latest rows, with the words this page still holds. */
export function doneOf(rows: TaskState[] | undefined, owner: string | undefined): DoneTask[] {
  const words = said.get(owner ?? '');
  return [...latestOf(rows).values()]
    .filter((r) => r.action === 'done')
    .sort((a, b) => b.id - a.id)
    .map((r) => ({
      key: r.key,
      id: r.id,
      date: r.date ?? undefined,
      team: r.team,
      area: isArea(r.area) ? r.area : 'timer',
      at: r.at,
      by: r.by,
      ...words?.get(stateKey(r.key, r.team)),
    }));
}

const PREFIX = 'timefly-saker-';

/**
 * Log-out: the drafts this tab holds, the words in memory, and the per-device lists Saker kept in this browser
 * before the server held them (#286).
 */
export function forgetSaker() {
  said.clear();
  for (const box of [globalThis.localStorage, globalThis.sessionStorage])
    try {
      for (const k of Object.keys(box ?? {})) if (k.startsWith(PREFIX)) box.removeItem(k);
    } catch {}
}

/** The finished tasks of a scope: the team's own, and the period's by the date each was about. */
export function doneIn(done: DoneTask[], scope: Scope, period: Period): DoneTask[] {
  const team = scope.team?.name ?? null;
  return done.filter(
    (t) =>
      t.team === team &&
      (period === 'current'
        ? !t.date || scope.days.includes(t.date)
        : !!t.date && !scope.days.includes(t.date)),
  );
}

/** A correction's draft, kept per task in this tab so a look-up of a phone number and back loses nothing (S06). */
export interface TimeDraft {
  date: string;
  time: string;
  reason: string;
}
const draftKey = (key: string) => `${PREFIX}draft-${key}`;
export const drafts = {
  read(key: string): TimeDraft | null {
    try {
      const v = JSON.parse(sessionStorage.getItem(draftKey(key)) ?? 'null');
      return v && typeof v.time === 'string' && typeof v.reason === 'string' && typeof v.date === 'string'
        ? v
        : null;
    } catch {
      return null;
    }
  },
  write(key: string, d: TimeDraft | null) {
    try {
      if (d) sessionStorage.setItem(draftKey(key), JSON.stringify(d));
      else sessionStorage.removeItem(draftKey(key));
    } catch {}
  },
};

/** A phone number a call can be placed to: digits, an optional +, at least eight digits (S06). */
export function dialable(phone: string | null | undefined): string | null {
  const p = (phone ?? '').replace(/[\s-]/g, '');
  return /^\+?\d{8,15}$/.test(p) ? p : null;
}

/** «4 t 35 min», «35 min», «8 t» */
export function hoursMin(min: number): string {
  const m = Math.max(0, Math.round(min));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return h && r ? `${h} t ${r} min` : h ? `${h} t` : `${r} min`;
}
